import React from 'react';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import {
  Header,
  Helmet,
  ThemeLayout,
  Content,
  Footer,
  Intro,
} from '@components';
import { ScrollToTop, Breadcrumb } from 'ech-components/src/components';
import { injectIntl, IntlShape, WrappedComponentProps } from 'react-intl';
import {
  Grid,
  GridItem,
  Spacer,
  Container,
  Main,
} from 'ech-components/src/helpers';
import { H1 } from 'ech-components/src/primitives';
import { options } from '@components/Content';
import { createLanguagePath, createRoutingURI } from '@scripts/utils/domain';
import { rem } from 'polished';
import { colors, mixins } from 'ech-components/src/core/styles/theme/default';
import { getRootTranslation } from '../../gatsby-helper';

const StyledTitle = styled(H1)``;

const StyledEventInfo = styled.time`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  color: ${colors.secondarySoftPurple800};
  font-size: ${rem(20)};
  line-height: 1.4;
  gap: 0 ${rem(14)};

  ${mixins.MQ('medium')} {
    flex-direction: row;
  }
`;

const StyledDate = styled.span`
  font-weight: 900;
`;

const StyledLocation = styled.span``;

const EventHubTemplate = ({
  data,
  intl,
  pageContext,
}: EventHubTemplateProps) => {
  const { contentfulEventHub, contentfulCampaignConfiguration } = data;

  if (!contentfulEventHub) return <>slug not found</>;

  const {
    seoTitle,
    seoDescription,
    navigationTitle,
    title,
    content,
    leadText,
    plz,
    location,
    language,
    eventDate,
  } = contentfulEventHub;
  const { campaignKey } = contentfulCampaignConfiguration;

  const pageUrl = createLanguagePath({
    switcher: pageContext.lang.switcher,
    language: intl.locale,
  });

  const breadcrumbs = [
    {
      title: intl.formatMessage({ id: 'page.navigation.events' }),
      href: createRoutingURI({
        root: getRootTranslation('events', intl.locale),
        slug: '',
        locale: intl.locale,
      }),
    },
    {
      title: navigationTitle,
      href: pageUrl,
    },
  ];

  const formattedDate = new Date(eventDate).toLocaleString(intl.locale, {
    dateStyle: 'long',
  });
  const address = [plz, location, language].filter(Boolean).join(' | ');

  return (
    <ThemeLayout
      customTheme={{
        campaign: {
          key: campaignKey,
        },
      }}
    >
      <Helmet
        locale={pageContext.lang.actual}
        langObj={pageContext.lang}
        description={seoDescription}
        title={seoTitle}
        matomo={pageContext.matomo}
      />
      <Header langObj={pageContext.lang} />
      <Main>
        <Spacer space="giga" />
        <Container>
          <Grid columns={12}>
            <GridItem
              size={{ zero: 12, medium: 10, large: 8 }}
              push={{ zero: 0, medium: 1, large: 2 }}
            >
              <Breadcrumb items={breadcrumbs} />
              <StyledTitle>{title}</StyledTitle>
              <StyledEventInfo>
                <StyledDate>{formattedDate}</StyledDate>
                {address && <StyledLocation>{address}</StyledLocation>}
              </StyledEventInfo>
              <Spacer space="deci" />
            </GridItem>
          </Grid>
          <Intro leadText={leadText} pageUrl={pageUrl} />
          {content ? (
            <section id="content">
              <Content content={content} options={options} />
            </section>
          ) : null}
        </Container>
        <Spacer space="peta" />
        <ScrollToTop
          backToTopText={intl.formatMessage({
            id: 'page.backToTop',
          })}
        />
        <Spacer space="peta" />
      </Main>
      <Footer langObj={pageContext.lang} />
    </ThemeLayout>
  );
};

export const query = graphql`
  query ContentfulEventHubTemplate(
    $slug: String
    $locale: String
    $campaignKey: String
  ) {
    contentfulCampaignConfiguration(
      node_locale: { eq: $locale }
      campaignKey: { eq: $campaignKey }
    ) {
      campaignName
      campaignKey
    }
    contentfulEventHub(slug: { eq: $slug }, node_locale: { eq: $locale }) {
      __typename
      node_locale
      seoDescription
      seoTitle
      navigationTitle
      title
      leadText {
        raw
      }
      slug
      provider
      eventDate
      plz
      location
      language
      content {
        ...contentfulContentEventHubFragment
      }
    }
  }
`;

export type EventHubTemplateProps = {
  data: any;
  intl: IntlShape;
  pageContext: any;
} & WrappedComponentProps;

export default injectIntl(EventHubTemplate);
